// web/src/lib/shifts.js
// Shaping a week of shifts into a grid.
//
// Pure functions only — no React, no fetch — so the arithmetic that decides
// what lands in which cell can be tested without a browser. The rules about
// what CONFLICTS are not here on purpose: the API works those out and sends
// them with each shift, because two implementations of "does this clash" drift,
// and the way a barn finds out is a groom who was told the week was fine.
//
// Dates are "YYYY-MM-DD" and times "HH:MM" throughout, both of which sort
// correctly as strings. Nothing here constructs a Date from a local timezone,
// so nothing here changes behavior on the day the clocks do.

/** Add days to an ISO date, purely. */
export function addDaysISO(iso, n) {
  const d = new Date(iso + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/**
 * The seven days of the grid, starting at `anchor`.
 *
 * Rolling, not calendar-aligned — the same choice the board made. A manager on
 * Thursday cares about the next seven days; a week that starts on Sunday spends
 * three of its columns on days that have already happened.
 */
export function weekFrom(anchor) {
  return Array.from({ length: 7 }, (_, i) => addDaysISO(anchor, i));
}

/** Minutes between two "HH:MM" times. Zero rather than NaN for junk. */
export function minutesBetween(start, end) {
  const p = (t) => {
    const m = /^(\d{2}):(\d{2})$/.exec(t || '');
    return m ? Number(m[1]) * 60 + Number(m[2]) : null;
  };
  const a = p(start);
  const b = p(end);
  if (a == null || b == null) return 0;
  return Math.max(0, b - a);
}

/** "6h 30m" — the form a barn already says hours in, not "6.5h". */
export function formatMinutes(total) {
  if (!total || total <= 0) return '0m';
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (!h) return `${m}m`;
  if (!m) return `${h}h`;
  return `${h}h ${m}m`;
}

/** "13:00" -> "1:00 PM". Matches the typed time field's echo. */
export function clock(hhmm) {
  const m = /^(\d{1,2}):(\d{2})/.exec(hhmm || '');
  if (!m) return '';
  const h24 = Number(m[1]);
  const h = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h}:${m[2]} ${h24 >= 12 ? 'PM' : 'AM'}`;
}

/** "7 AM – 12 PM", dropping ":00" because most shifts start on the hour. */
export function timeRange(start, end) {
  const tidy = (t) => clock(t).replace(':00', '');
  return `${tidy(start)} – ${tidy(end)}`;
}

/** The key a cell is found by. One place, so the writer and reader agree. */
export function cellKey(userId, date) {
  return `${userId || 'open'}|${date}`;
}

/**
 * shifts -> { "userId|date": [shift, ...] }, each cell sorted by start time.
 *
 * Open shifts collect under the key "open", which is why cellKey coerces a null
 * userId rather than leaving it to each caller: a cell keyed "null|2026-09-14"
 * in one place and "open|2026-09-14" in another renders an empty grid and looks
 * like an API problem.
 */
export function byCell(shifts = []) {
  const map = new Map();
  for (const s of shifts) {
    const k = cellKey(s.userId, s.date);
    const list = map.get(k);
    if (list) list.push(s);
    else map.set(k, [s]);
  }
  for (const list of map.values()) list.sort((a, b) => a.startTime.localeCompare(b.startTime));
  return map;
}

/**
 * The rows of the grid, in the order they are read.
 *
 * Open shifts first when there are any — unstaffed work is the thing a manager
 * is looking for, and putting it under the Ws means finding it by scrolling.
 * Everyone else alphabetically, including people with no shifts: an empty row
 * is how you notice you forgot somebody, and hiding it hides that.
 */
export function gridRows(staff = [], shifts = []) {
  const rows = staff
    .slice()
    .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }))
    .map((s) => ({ key: s.userId, userId: s.userId, name: s.name || 'Unnamed', role: s.role }));

  if (shifts.some((s) => !s.userId)) {
    rows.unshift({ key: 'open', userId: null, name: 'Open shifts', role: null, open: true });
  }
  return rows;
}

/**
 * Scheduled minutes, counting each person's hours on a day ONCE.
 *
 * "At the barn 7-2" with "grain 1-2" inside it is seven hours of one person,
 * not eight. Adding shift lengths up was right when a person could only have
 * one block at a time; now that a job can sit inside their hours, the honest
 * total is the union of their blocks that day. Open shifts have no person to
 * union under, so each counts on its own — two open feeds are two gaps.
 */
export function unionMinutes(shifts = []) {
  const groups = new Map();
  for (const s of shifts) {
    const key = s.userId ? `${s.userId}|${s.date}` : `open|${s.id}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(s);
  }
  let total = 0;
  for (const list of groups.values()) {
    const spans = list
      .map((s) => [toMinutes(s.startTime), toMinutes(s.endTime)])
      .filter(([a, b]) => a != null && b != null && b > a)
      .sort((x, y) => x[0] - y[0]);
    let from = null;
    let to = null;
    for (const [a, b] of spans) {
      if (to == null || a > to) {
        if (to != null) total += to - from;
        from = a;
        to = b;
      } else if (b > to) {
        to = b;
      }
    }
    if (to != null) total += to - from;
  }
  return total;
}

/** Total scheduled minutes for one person across the week. */
export function rowMinutes(shifts, userId) {
  return unionMinutes(shifts.filter((s) => (s.userId || null) === (userId || null)));
}

/** Total scheduled minutes on one day, everybody. */
export function columnMinutes(shifts, date) {
  return unionMinutes(shifts.filter((s) => s.date === date));
}

/** How many shifts one person has this week. */
export function countFor(shifts, userId) {
  return shifts.filter((s) => (s.userId || null) === (userId || null)).length;
}

/**
 * How many PEOPLE are on that day — not how many shifts.
 *
 * Distinct, because somebody doing morning feed and evening feed is one person
 * covering a day, and "4 on" when it is two people working doubles is the
 * number that gets a barn into trouble. Open shifts are nobody and are counted
 * nowhere: they are the gap, and the grid already gives them their own row.
 */
export function peopleOn(shifts, date) {
  const who = new Set();
  for (const s of shifts) {
    if (s.date === date && s.userId) who.add(s.userId);
  }
  return who.size;
}

/** "2 shifts" / "1 shift" — the unit said out loud, never a bare number. */
export function shiftsLabel(n) {
  return `${n} shift${n === 1 ? '' : 's'}`;
}

/**
 * The time off covering one person on one day.
 *
 * Both approved and pending come back — a manager needs to see a request
 * BEFORE building over it, which is the whole reason for showing it — so the
 * caller decides how loudly to draw each. Only 'approved' actually blocks, and
 * that decision belongs to the API.
 */
export function timeOffOn(timeOff = [], userId, date) {
  return timeOff.filter((o) => o.userId === userId && date >= o.startDate && date <= o.endDate);
}

/** A trainer's lessons on one day, for the read-only context line. */
export function lessonsOn(lessons = [], userId, date) {
  return lessons
    .filter((l) => l.userId === userId && l.date === date)
    .sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));
}

/**
 * How many shifts are waiting to be announced.
 *
 * Never-published and published-then-edited are one number on purpose: to the
 * person about to press Publish they mean the same thing — people don't know
 * about this yet.
 */
export function unannouncedCount(shifts = []) {
  return shifts.filter((s) => s.unannounced).length;
}

/** The worst thing wrong with a shift, or null. Cells show one badge. */
export function worstConflict(shift) {
  const list = shift?.conflicts || [];
  return list.find((c) => c.severity === 'blocking') || list[0] || null;
}

/**
 * Sensible times for a new shift in a cell.
 *
 * Prefers the shift type's own hours, because "Morning feed" already means
 * 6–9 and retyping it every time is the thing a template exists to avoid.
 */
export function defaultTimes(type) {
  return {
    startTime: type?.startTime || '08:00',
    endTime: type?.endTime || '12:00',
  };
}

// ---------------------------------------------------------------------------
// The day view
//
// A week is read as blocks — "who's on morning feed" — which is why the board's
// week has no time axis. A DAY is the zoom where the axis earns its keep: it is
// where you see that nobody is here between 12 and 3, and that Dana's day has a
// two-hour hole in the middle of it.
// ---------------------------------------------------------------------------

/** "HH:MM" -> minutes since midnight, or null. */
export function toMinutes(hhmm) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm || '');
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}

/** minutes -> "7 AM" / "3:30 PM", for the axis ticks. */
export function tickLabel(min) {
  const h24 = Math.floor(min / 60) % 24;
  const m = min % 60;
  const h = h24 % 12 === 0 ? 12 : h24 % 12;
  return m ? `${h}:${String(m).padStart(2, '0')} ${h24 >= 12 ? 'PM' : 'AM'}` : `${h} ${h24 >= 12 ? 'PM' : 'AM'}`;
}

/** A barn day when there is nothing to measure: 6am to 9pm. */
const DEFAULT_AXIS = { start: 6 * 60, end: 21 * 60 };

/**
 * The time range the day is drawn across.
 *
 * Derived from everything on the day — shifts, time off, lessons — not just
 * shifts. An "off 1-6" band that falls outside the axis would be clipped to
 * nothing, and the row would look free at exactly the hours it isn't.
 *
 * Rounded out to whole hours so the ticks land on the hour, and never narrower
 * than four hours: a day with one 20-minute night check stretched across the
 * full width reads as a shift that took all evening.
 */
export function dayAxis(items = []) {
  let lo = null;
  let hi = null;
  for (const it of items) {
    const a = toMinutes(it.startTime);
    const b = toMinutes(it.endTime);
    if (a == null || b == null) continue;
    lo = lo == null ? a : Math.min(lo, a);
    hi = hi == null ? b : Math.max(hi, b);
  }
  if (lo == null || hi == null) return { ...DEFAULT_AXIS };

  let start = Math.floor(lo / 60) * 60;
  let end = Math.ceil(hi / 60) * 60;

  // Widen forward first, then back, alternating. Always widening backwards put
  // a 6-9am shift on an axis running 5am to 9am — pinned to the right-hand edge
  // with an empty hour before it, which reads as a morning that is nearly over.
  // Growing the end first keeps the work where the eye expects it.
  let forward = true;
  while (end - start < 240 && (start > 0 || end < 24 * 60)) {
    if (forward && end < 24 * 60) end += 60;
    else if (start > 0) start -= 60;
    else end += 60;
    forward = !forward;
  }
  return { start: Math.max(0, start), end: Math.min(end, 24 * 60) };
}

/** Whole-hour ticks across an axis. */
export function axisTicks(axis, everyMinutes = 60) {
  const out = [];
  for (let m = axis.start; m <= axis.end; m += everyMinutes) out.push(m);
  return out;
}

/**
 * Where a bar sits on the axis, as percentages.
 *
 * Clamped to the axis rather than allowed to run off it: a shift that starts
 * before the first tick should butt against the edge, not be drawn at a
 * negative offset where the browser will simply not show it.
 */
export function barGeometry(item, axis) {
  const span = axis.end - axis.start;
  if (span <= 0) return { left: 0, width: 0 };
  const a = toMinutes(item.startTime);
  const b = toMinutes(item.endTime);
  if (a == null || b == null) return { left: 0, width: 0 };
  const from = Math.max(a, axis.start);
  const to = Math.min(b, axis.end);
  if (to <= from) return { left: 0, width: 0 };
  return {
    left: ((from - axis.start) / span) * 100,
    width: ((to - from) / span) * 100,
  };
}

/**
 * How many people are on at each step across the day.
 *
 * People, not shifts — somebody doing morning and evening feed is one person,
 * and a ribbon that said "2" at both ends of their day would be describing a
 * barn with twice the staff it has.
 *
 * Open shifts are counted separately. They are work that is happening in the
 * plan and by nobody in fact, which is a different thing from an empty hour and
 * has to read differently.
 */
export function coverageSeries(shifts = [], axis, stepMinutes = 30) {
  const out = [];
  for (let m = axis.start; m < axis.end; m += stepMinutes) {
    const who = new Set();
    let open = 0;
    for (const s of shifts) {
      const a = toMinutes(s.startTime);
      const b = toMinutes(s.endTime);
      if (a == null || b == null) continue;
      // Half-open, the same rule conflicts use: a shift ending at 12:00 is not
      // still running in the 12:00 step.
      if (a > m || b <= m) continue;
      if (s.userId) who.add(s.userId);
      else open += 1;
    }
    out.push({ start: m, end: m + stepMinutes, people: who.size, open });
  }
  return out;
}

/**
 * The empty stretches in one person's day, between their first and last shift.
 *
 * Only the middle: time before somebody starts and after they finish is not a
 * gap, it is the rest of their life. A two-hour hole between turnout and
 * evening feed is the thing worth seeing.
 */
export function gapsFor(shifts = [], minMinutes = 30) {
  const mine = shifts
    .map((s) => ({ a: toMinutes(s.startTime), b: toMinutes(s.endTime) }))
    .filter((x) => x.a != null && x.b != null)
    .sort((x, y) => x.a - y.a);
  if (mine.length < 2) return [];

  const out = [];
  let edge = mine[0].b;
  for (let i = 1; i < mine.length; i++) {
    const next = mine[i];
    if (next.a - edge >= minMinutes) out.push({ start: edge, end: next.a });
    edge = Math.max(edge, next.b);
  }
  return out;
}

/**
 * Stack overlapping bars into lanes so none is hidden behind another.
 *
 * Two people on evening feed at the same time is not an edge case, it is the
 * answer to "does one person cover this" — and drawn at identical geometry the
 * second one is invisible behind the first. A row that silently shows one of
 * two bodies is worse than no row at all.
 *
 * Greedy interval partitioning: walk in start order and drop each item into the
 * first lane whose last bar has finished. Half-open, so a handover shares a
 * lane rather than opening a second one.
 *
 * Returns { lanes, laneOf } — the count so the row can be sized, and a lookup
 * by id so the caller doesn't have to re-derive the order.
 */
export function assignLanes(items = []) {
  const sorted = items
    .map((it, i) => ({ it, i, a: toMinutes(it.startTime), b: toMinutes(it.endTime) }))
    .filter((x) => x.a != null && x.b != null)
    .sort((x, y) => x.a - y.a || x.b - y.b || x.i - y.i);

  const ends = []; // last end time in each lane
  const laneOf = new Map();

  for (const x of sorted) {
    let lane = ends.findIndex((end) => end <= x.a);
    if (lane === -1) {
      lane = ends.length;
      ends.push(x.b);
    } else {
      ends[lane] = x.b;
    }
    laneOf.set(x.it.id ?? x.i, lane);
  }

  return { lanes: Math.max(1, ends.length), laneOf };
}
